class ProductDetailsDrawer extends HTMLElement {
    constructor() {
        super();
        this.onKeyUp = this.onKeyUp.bind(this);
        this.variantId = null;
        // Per-variant debounce timers so rapid +/- clicks coalesce into a single
        // /cart/update.js request (prevents Shopify cart-API rate limiting / 429).
        this._cartTimers = {};
        this._cartDebounceMs = 450;
    }

    connectedCallback() {
        this.overlay = this.querySelector('[data-product-drawer-overlay]');
        this.closeButton = this.querySelector('[data-product-drawer-close]');
        this.contentEl = this.querySelector('#ProductDetailsDrawer-Content');

        if (this.overlay) {
            this.overlay.addEventListener('click', () => this.close());
        }

        if (this.closeButton) {
            this.closeButton.addEventListener('click', () => this.close());
        }

        this.contentEl?.addEventListener('click', (e) => this.handleContentClick(e));

        // Keep the mobile variant-sheet "+" badge in sync with the cart. quick-add-bulk
        // steppers (in the sheet) publish cartUpdate when a size is added/removed.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
            subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
                if (event && event.cartData) {
                    this.updateVariantSheetCount(event.cartData);
                } else {
                    fetch('/cart.js', { headers: { Accept: 'application/json' } })
                        .then((r) => r.json())
                        .then((cart) => this.updateVariantSheetCount(cart))
                        .catch(() => {});
                }
            });
        }
    }

    openVariantSheet() {
        const sheet = this.contentEl?.querySelector('[data-variant-sheet]');
        const backdrop = this.contentEl?.querySelector('[data-variant-sheet-backdrop]');
        const trigger = this.contentEl?.querySelector('[data-variant-sheet-trigger]');
        if (!sheet) return;

        // Backdrop visibility is driven purely by the .is-open class (opacity/visibility),
        // not display/hidden — keeps it bulletproof against display quirks.
        sheet.hidden = false;
        // Next frame so the initial (translated/transparent) state is painted before transitioning.
        requestAnimationFrame(() => {
            if (backdrop) backdrop.classList.add('is-open');
            sheet.classList.add('is-open');
        });
        if (trigger) trigger.setAttribute('aria-expanded', 'true');
    }

    closeVariantSheet() {
        const sheet = this.contentEl?.querySelector('[data-variant-sheet]');
        const backdrop = this.contentEl?.querySelector('[data-variant-sheet-backdrop]');
        const trigger = this.contentEl?.querySelector('[data-variant-sheet-trigger]');
        if (!sheet || sheet.hidden) return;

        sheet.classList.remove('is-open');
        if (backdrop) backdrop.classList.remove('is-open');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');

        const onEnd = () => {
            sheet.hidden = true;
            sheet.removeEventListener('transitionend', onEnd);
        };
        sheet.addEventListener('transitionend', onEnd);
    }

    // Update the mobile "+" badge: total of this product's variants in the cart.
    updateVariantSheetCount(cart) {
        const trigger = this.contentEl?.querySelector('[data-variant-sheet-trigger]');
        const body = this.contentEl?.querySelector('[data-variant-sheet] .product-size-list');
        if (!trigger || !body || !cart) return;

        const ids = new Set(
            Array.from(body.querySelectorAll('.quantity__input[data-quantity-variant-id]')).map((i) =>
                i.getAttribute('data-quantity-variant-id')
            )
        );
        let total = 0;
        (cart.items || []).forEach((it) => {
            if (ids.has(String(it.variant_id)) || ids.has(String(it.id))) total += it.quantity;
        });

        const countEl = trigger.querySelector('.product-details__variant-trigger-count');
        if (total > 0) {
            trigger.classList.add('product-details__variant-trigger--has-items');
            if (countEl) {
                countEl.textContent = total;
                countEl.hidden = false;
            }
        } else {
            trigger.classList.remove('product-details__variant-trigger--has-items');
            if (countEl) countEl.hidden = true;
        }
    }

    open(html, variantId) {
        if (!this.contentEl) return;

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && cartDrawer.classList.contains('active')) {
            cartDrawer.close();
        }

        this.variantId = variantId || null;
        this.contentEl.innerHTML = html;

        if (!this.variantId) {
            const root = this.contentEl.querySelector('[data-variant-id]');
            if (root?.dataset.variantId) {
                this.variantId = root.dataset.variantId;
            } else {
                const btn = this.contentEl.querySelector('[data-add-to-cart]');
                if (btn) this.variantId = btn.getAttribute('data-add-to-cart');
            }
        }

        this.syncQuantityFromCart();

        this.classList.add('active', 'animate');
        document.body.classList.add('overflow-hidden');
        document.addEventListener('keyup', this.onKeyUp);
    }

    swapContent(html, variantId) {
        if (!this.contentEl) return;

        // If drawer is not open, just use regular open
        if (!this.classList.contains('active')) {
            this.open(html, variantId);
            return;
        }

        const scrollContainer = this.contentEl.closest('.drawer__inner');

        // Slide out current content
        this.contentEl.classList.add('slide-out');

        setTimeout(() => {
            // Replace content
            this.variantId = variantId || null;
            this.contentEl.innerHTML = html;

            if (!this.variantId) {
                const root = this.contentEl.querySelector('[data-variant-id]');
                if (root?.dataset.variantId) {
                    this.variantId = root.dataset.variantId;
                } else {
                    const btn = this.contentEl.querySelector('[data-add-to-cart]');
                    if (btn) this.variantId = btn.getAttribute('data-add-to-cart');
                }
            }

            this.syncQuantityFromCart();

            // Scroll to top
            if (scrollContainer) scrollContainer.scrollTop = 0;

            // Prepare slide-in position (off-screen right)
            this.contentEl.classList.remove('slide-out');
            this.contentEl.classList.add('slide-in-prepare');

            // Force reflow so the browser registers the starting position
            this.contentEl.offsetHeight;

            // Animate in
            this.contentEl.classList.remove('slide-in-prepare');
            this.contentEl.classList.add('slide-in');

            const cleanup = () => {
                this.contentEl.classList.remove('slide-in');
                this.contentEl.removeEventListener('transitionend', cleanup);
            };
            this.contentEl.addEventListener('transitionend', cleanup);
        }, 200); // Match slide-out duration
    }

    close() {
        this.classList.remove('active', 'animate');
        document.body.classList.remove('overflow-hidden');
        document.removeEventListener('keyup', this.onKeyUp);

        if (this.contentEl) {
            this.contentEl.classList.remove('slide-out', 'slide-in-prepare', 'slide-in');
            this.contentEl.innerHTML = '';
        }

        this.variantId = null;
    }

    onKeyUp(event) {
        if (event.code === 'Escape' || event.key === 'Escape') {
            this.close();
        }
    }

    updateQuantitySelectorState(quantity) {
        const selector = this.contentEl?.querySelector('.product-details__quantity-selector');
        if (!selector) return;

        if (quantity > 0) {
            selector.classList.add('has-items');
        } else {
            selector.classList.remove('has-items');
        }
    }

    updateRelatedProductQuantity(variantId, quantity) {
        const card = this.contentEl?.querySelector(`[data-related-variant-id="${variantId}"]`);
        if (!card) return;

        const quantityWrapper = card.querySelector('[data-related-quantity]');
        const qtyDisplay = card.querySelector('[data-related-qty-display]');

        if (qtyDisplay) {
            qtyDisplay.textContent = quantity;
        }

        if (quantityWrapper) {
            if (quantity > 0) {
                quantityWrapper.classList.add('has-items');
            } else {
                quantityWrapper.classList.remove('has-items');
            }
        }
    }

    async syncQuantityFromCart() {
        if (!this.contentEl) return;

        try {
            const res = await fetch('/cart.js');
            const cart = await res.json();

            // Sync main product quantity
            if (this.variantId) {
                const qtyInput = this.contentEl.querySelector('.product-details__quantity-input');
                if (qtyInput) {
                    const variantIdNum = Number(this.variantId);
                    const line = cart.items.find(
                        (item) => item.id === variantIdNum || item.variant_id === variantIdNum
                    );

                    const quantity = line ? line.quantity : 0;
                    qtyInput.value = quantity;
                    qtyInput.dataset.cartQuantity = String(quantity);
                    this.updateQuantitySelectorState(quantity);
                }
            }

            // Sync related products quantities
            const relatedCards = this.contentEl.querySelectorAll('[data-related-variant-id]');
            relatedCards.forEach(card => {
                const relatedVariantId = card.dataset.relatedVariantId;
                const line = cart.items.find(
                    (item) => String(item.id) === relatedVariantId || String(item.variant_id) === relatedVariantId
                );
                const quantity = line ? line.quantity : 0;
                this.updateRelatedProductQuantity(relatedVariantId, quantity);
            });

            // Sync every per-size / footer quick-add-bulk stepper (the drawer content is
            // cloned from a page-load template, so its counts can be stale on open).
            this.syncAllSteppersFromCart(cart);

            // Mobile multi-variant "+" badge
            this.updateVariantSheetCount(cart);

        } catch (e) {
            console.error('Failed to sync drawer quantities from cart', e);
        }
    }

    syncAllSteppersFromCart(cart) {
        const inputs = this.contentEl?.querySelectorAll('.quantity__input[data-quantity-variant-id]');
        if (!inputs || !cart) return;
        inputs.forEach((input) => {
            const vid = input.getAttribute('data-quantity-variant-id');
            const line = (cart.items || []).find(
                (it) => String(it.variant_id) === vid || String(it.id) === vid
            );
            const q = line ? line.quantity : 0;
            input.value = q;
            input.setAttribute('value', q);
            input.dataset.cartQuantity = String(q);
            const qi = input.closest('quantity-input');
            if (qi) qi.classList.toggle('quantity--has-items', q > 0);
        });
    }

    updateCartQuantity(variantId, quantity) {
        if (!variantId) return;

        const newQty = Math.max(0, parseInt(quantity || '0', 10));

        // Update UI immediately for main product
        if (String(variantId) === String(this.variantId)) {
            this.updateQuantitySelectorState(newQty);
        }

        // Debounce the network write per variant: spamming +/- only sends the
        // final quantity once the clicks settle, instead of one POST per click.
        const key = String(variantId);
        if (this._cartTimers[key]) clearTimeout(this._cartTimers[key]);
        this._cartTimers[key] = setTimeout(() => {
            delete this._cartTimers[key];
            this._commitCartQuantity(variantId, newQty);
        }, this._cartDebounceMs);
    }

    async _commitCartQuantity(variantId, newQty) {
        try {
            const body = { updates: { [variantId]: newQty } };

            const res = await fetch('/cart/update.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });

            if (!res.ok) {
                // Surface the real reason (e.g. "...sold out") instead of failing
                // silently, and revert the optimistic UI to the actual cart state.
                const errBody = await res.json().catch(() => ({}));
                const message = errBody.description || errBody.message || 'Could not update the cart.';
                console.error('Cart drawer error:', res.status, message);
                this.showDrawerError(message);
                this.syncQuantityFromCart();
                return;
            }

            this.clearDrawerError();

            const cart = await res.json();
            this.refreshCartUI(cart, variantId);

            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartUpdate, {
                    source: 'product-details-drawer',
                    cartData: cart,
                    variantId: variantId
                });
            }

            document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart } }));

        } catch (e) {
            console.error('Error in updateCartQuantity', e);
        }
    }

    showDrawerError(message) {
        const el = this.contentEl?.querySelector('[data-drawer-cart-error]');
        if (!el) return;
        el.textContent = message || 'Could not update the cart.';
        el.hidden = false;
    }

    clearDrawerError() {
        const el = this.contentEl?.querySelector('[data-drawer-cart-error]');
        if (!el) return;
        el.hidden = true;
        el.textContent = '';
    }

    updateCartIcon(cart) {
        const cartIcon = document.querySelector('#cart-icon-bubble');
        if (!cartIcon) return;

        let bubble = cartIcon.querySelector('.cart-count-bubble');
        const itemCount = cart.item_count || 0;

        if (itemCount === 0) {
            if (bubble) bubble.remove();
            return;
        }

        if (!bubble) {
            bubble = document.createElement('div');
            bubble.className = 'cart-count-bubble';
            bubble.innerHTML =
                '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
            cartIcon.appendChild(bubble);
        }

        const spans = bubble.querySelectorAll('span');
        const visible = spans[0];
        const sr = spans[1];

        if (visible) visible.textContent = itemCount;
        if (sr) sr.textContent = `${itemCount} items`;
    }

    refreshCartUI(cart, variantId) {
        if (!cart) return;

        this.updateCartIcon(cart);
        if (variantId) {
            this.updateListCardQuantity(cart, variantId);
        }

        // Sync main product input
        const qtyInput = this.contentEl?.querySelector('.product-details__quantity-input');
        if (qtyInput && String(variantId) === String(this.variantId)) {
            const line = cart.items.find(
                (item) =>
                    String(item.id) === String(variantId) ||
                    String(item.variant_id) === String(variantId)
            );
            const q = line ? line.quantity : 0;
            qtyInput.value = q;
            qtyInput.dataset.cartQuantity = String(q);
            this.updateQuantitySelectorState(q);
        }

        // Sync related product quantities
        this.updateRelatedProductQuantity(variantId,
            cart.items.find(item =>
                String(item.id) === String(variantId) ||
                String(item.variant_id) === String(variantId)
            )?.quantity || 0
        );
    }

    updateListCardQuantity(cart, variantId) {
        const variantStr = String(variantId);

        const line = cart.items.find(
            (item) =>
                String(item.id) === variantStr ||
                String(item.variant_id) === variantStr
        );

        const lineQty = line ? line.quantity : 0;

        const inputs = document.querySelectorAll(
            `.quantity__input[data-quantity-variant-id="${variantStr}"]`
        );

        inputs.forEach((input) => {
            input.value = lineQty;
            input.setAttribute('value', lineQty);
            input.dataset.cartQuantity = String(lineQty);

            const quantityInput = input.closest('quantity-input');
            if (quantityInput) {
                if (lineQty > 0) {
                    quantityInput.classList.add('quantity--has-items');
                } else {
                    quantityInput.classList.remove('quantity--has-items');
                }
            }

            const quickAdd = input.closest('quick-add-bulk');
            if (quickAdd) {
                const minusBtn = quickAdd.querySelector('.quantity__button[name="minus"]');
                if (minusBtn) {
                    const min = parseInt(input.getAttribute('min') || '0', 10);
                    if (lineQty <= min) {
                        minusBtn.classList.add('disabled');
                    } else {
                        minusBtn.classList.remove('disabled');
                    }
                }
            }
        });
    }

    handleContentClick(event) {
        // ----- Mobile variant sheet open/close -----
        if (event.target.closest('[data-variant-sheet-trigger]')) {
            event.preventDefault();
            this.openVariantSheet();
            return;
        }
        if (
            event.target.closest('[data-variant-sheet-close]') ||
            event.target.closest('[data-variant-sheet-backdrop]')
        ) {
            event.preventDefault();
            this.closeVariantSheet();
            return;
        }

        const qtyInput = this.contentEl.querySelector('.product-details__quantity-input');

        // ----- Main product minus -----
        if (event.target.closest('[data-quantity-minus]')) {
            if (!qtyInput) return;

            let current = parseInt(qtyInput.value || '0', 10);
            current = isNaN(current) ? 0 : current;
            current = Math.max(0, current - 1);

            qtyInput.value = current;

            if (this.variantId) {
                this.updateCartQuantity(this.variantId, current);
            }

            return;
        }

        // ----- Main product plus -----
        if (event.target.closest('[data-quantity-plus]')) {
            if (!qtyInput) return;

            let current = parseInt(qtyInput.value || '0', 10);
            current = isNaN(current) ? 0 : current;
            current = current + 1;

            qtyInput.value = current;

            if (this.variantId) {
                this.updateCartQuantity(this.variantId, current);
            }

            return;
        }

        // ----- Add to cart button (price button) -----
        const addToCartBtn = event.target.closest('[data-add-to-cart]');
        if (addToCartBtn) {
            event.preventDefault();

            const variantId = addToCartBtn.getAttribute('data-add-to-cart') || this.variantId;

            let quantity = 1;
            if (qtyInput) {
                quantity = parseInt(qtyInput.value || '0', 10);
                if (isNaN(quantity) || quantity < 0) quantity = 0;
                if (quantity === 0) quantity = 1;
            }

            if (variantId) {
                this.updateCartQuantity(variantId, quantity);
            }

            return;
        }

        // ----- Related product minus -----
        const relatedMinus = event.target.closest('[data-related-minus]');
        if (relatedMinus) {
            event.preventDefault();
            event.stopPropagation();

            const quantityWrapper = relatedMinus.closest('[data-related-quantity]');
            const variantId = quantityWrapper?.dataset.variantId;
            const qtyDisplay = quantityWrapper?.querySelector('[data-related-qty-display]');

            if (variantId && qtyDisplay) {
                let current = parseInt(qtyDisplay.textContent || '0', 10);
                current = Math.max(0, current - 1);
                qtyDisplay.textContent = current;

                // Update UI immediately
                if (current > 0) {
                    quantityWrapper.classList.add('has-items');
                } else {
                    quantityWrapper.classList.remove('has-items');
                }

                this.updateCartQuantity(variantId, current);
            }

            return;
        }

        // ----- Related product plus -----
        const relatedPlus = event.target.closest('[data-related-plus]');
        if (relatedPlus) {
            event.preventDefault();
            event.stopPropagation();

            const quantityWrapper = relatedPlus.closest('[data-related-quantity]');
            const variantId = quantityWrapper?.dataset.variantId;
            const qtyDisplay = quantityWrapper?.querySelector('[data-related-qty-display]');

            if (variantId && qtyDisplay) {
                let current = parseInt(qtyDisplay.textContent || '0', 10);
                current = current + 1;
                qtyDisplay.textContent = current;

                // Update UI immediately
                quantityWrapper.classList.add('has-items');

                this.updateCartQuantity(variantId, current);
            }

            return;
        }

        // ----- Copy link -----
        const copyLinkBtn = event.target.closest('[data-copy-link]');
        if (copyLinkBtn) {
            const url = copyLinkBtn.getAttribute('data-copy-link');
            if (!url) return;

            navigator.clipboard.writeText(url).then(() => {
                copyLinkBtn.setAttribute('aria-label', 'Link copied!');
                setTimeout(() => {
                    copyLinkBtn.setAttribute('aria-label', 'Copy link');
                }, 2000);
            });

            return;
        }
    }
}

if (!customElements.get('product-details-drawer')) {
    customElements.define('product-details-drawer', ProductDetailsDrawer);
}

// Delegate clicks from product cards
document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-meal-product-trigger]');
    if (!trigger) return;

    if (event.target.closest('button, input, .quantity, quick-add-bulk')) return;

    const productHandle =
        trigger.getAttribute('data-product-handle') ||
        trigger.getAttribute('data-meal-product-id');

    if (!productHandle) return;

    let template = document.querySelector(
        `template[data-product-handle="${productHandle}"][data-product-drawer-template]`
    );

    if (!template) {
        template = document.getElementById(`ProductDetailsTemplate-${productHandle}`);
    }

    const drawer = document.querySelector('product-details-drawer');
    if (!drawer) return;

    const isAlreadyOpen = drawer.classList.contains('active');

    if (!template) {
        // No pre-rendered template — fetch drawer content dynamically
        event.preventDefault();

        if (!isAlreadyOpen) {
            drawer.contentEl.innerHTML = '<div class="product-details-loading" style="display:flex;align-items:center;justify-content:center;min-height:200px;"><span>Loading...</span></div>';
            drawer.classList.add('active', 'animate');
            document.body.classList.add('overflow-hidden');
        }

        const rootPath = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        fetch(`${rootPath}products/${productHandle}?section_id=product-drawer-fetch`)
            .then((r) => r.text())
            .then((html) => {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                const content = doc.querySelector('.product-details');
                const finalHtml = content ? content.outerHTML : html;

                if (isAlreadyOpen) {
                    drawer.swapContent(finalHtml);
                } else {
                    drawer.open(finalHtml);
                }
            })
            .catch(() => {
                drawer.close();
                window.location.href = trigger.href || `/products/${productHandle}`;
            });
        return;
    }

    const variantId = template.dataset.variantId;

    event.preventDefault();
    if (isAlreadyOpen) {
        drawer.swapContent(template.innerHTML, variantId);
    } else {
        drawer.open(template.innerHTML, variantId);
    }
});

// Let a vertical mouse wheel scroll the "You may also like" row horizontally.
// Delegated so it works for the dynamically-loaded drawer content.
document.addEventListener('wheel', (event) => {
    const grid = event.target.closest?.('.product-details__related-grid');
    if (!grid || !event.deltaY) return;
    if (grid.scrollWidth <= grid.clientWidth) return; // nothing to scroll
    grid.scrollLeft += event.deltaY;
    event.preventDefault();
}, { passive: false });